import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../core/services/auth.service';
import { UserStatusCheckerService } from './user-status-checker.service';
import { RbacService } from './rbac.service';

@Injectable({ providedIn: 'root' })
export class IdleWatcherService {
  private idleTimeout: any = null;
  private readonly DEFAULT_IDLE_LIMIT_MS = 5 * 60_000;      // 5 minutes
  private readonly REMEMBER_ME_IDLE_LIMIT_MS = 8 * 60 * 60_000; // 8 hours
  private idleLimitMs = this.DEFAULT_IDLE_LIMIT_MS;
  private listenersAttached = false;
  private storageListenerBound = this.onStorageChange.bind(this);
  private activityChannel: BroadcastChannel | null = null;

  constructor(
    private ngZone: NgZone,
    private router: Router,
    private authService: AuthService,
    private userStatusChecker: UserStatusCheckerService,
    private rbacService: RbacService
  ) {}

  start(rememberMe?: boolean): void {
    this.idleLimitMs = rememberMe ? this.REMEMBER_ME_IDLE_LIMIT_MS : this.DEFAULT_IDLE_LIMIT_MS;

    this.resetTimer();

    if (this.listenersAttached) { return; }

    this.ngZone.runOutsideAngular(() => {
      ['mousemove', 'keydown', 'click', 'scroll'].forEach(evt =>
        document.addEventListener(evt, this.onLocalActivity, { passive: true })
      );

      window.addEventListener('storage', this.storageListenerBound);

      // cross-tab activity relay — any tab's activity resets every tab's clock
      this.activityChannel = new BroadcastChannel('idle-watcher-activity');
      this.activityChannel.onmessage = () => this.resetTimer();
    });

    this.listenersAttached = true;
  }

  notifyActivity(): void {
    if (!this.listenersAttached) { return; }
    this.resetTimer();
    this.activityChannel?.postMessage('activity');
  }

  // local DOM activity: reset our own timer AND tell other tabs
  private onLocalActivity = () => {
    this.resetTimer();
    this.activityChannel?.postMessage('activity');
  };

  private resetTimer(): void {
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
    }
    this.idleTimeout = setTimeout(() => {
      this.ngZone.run(() => {
        this.triggerLogout();
      });
    }, this.idleLimitMs);
  }

  private triggerLogout(): void {
    this.userStatusChecker.stopPolling();
    this.authService.clearSession();
    this.rbacService.reset();
    this.stop();
    this.router.navigate(['/login']);
  }

  private onStorageChange(event: StorageEvent): void {
    if (event.key === 'sessionId' && event.newValue === null) {
      this.ngZone.run(() => {
        this.stop();
        this.router.navigate(['/login']);
      });
    }
  }

  stop(): void {
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
    }
    this.idleTimeout = null;

    if (this.listenersAttached) {
      ['mousemove', 'keydown', 'click', 'scroll'].forEach(evt =>
        document.removeEventListener(evt, this.onLocalActivity)
      );
      window.removeEventListener('storage', this.storageListenerBound);
      this.activityChannel?.close();
      this.activityChannel = null;
      this.listenersAttached = false;
    }
  }
}
